// GE-258 
import GridContainer, { GridContainerProps } from "@bToC/Overrides/Components/CustomGridContainer";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import ListItem, { UnorderedList, UnorderedListProps } from "@insite/mobius/Lists";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    attributesToShow = "attributesToShow"
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.attributesToShow]: number;
    };
}

type Props = OwnProps & WidgetProps & HasProduct;

export interface ProductDetailsTopNAttributesStyles {
    wrapper?: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;
    unorderedList?: UnorderedListProps;
    condensed?: boolean;
    listItem?: InjectableCss;
    label?: InjectableCss;
}

export const attributesStyles: ProductDetailsTopNAttributesStyles = {
    attributeTypeLabelText: {
        weight: "bold",
        css: css`
            padding-right:5px;
        `,
    },
    attributeTypeValuesText: {
        css: css`
            margin-bottom: 5px;
        `,
    },
    unorderedList: {
        css: css`
            list-style: none;
            columns: 2;
            -webkit-columns: 2;
            -moz-columns: 2;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                        display: grid;
                    `,
                        css`
                        display:grid;
                    `,
                        null,
                        null,
                    ],
                    "minMax",
                )}
        `,
    },
    listItem: {
        css: css`
            ::before {
                content: "⬤";
                color: #016EAF;
                display: inline-block;
                width: 2em;
                contain: layout;
                margin-left: -5em;
                font-size: 8px;
            }
        `
    }
};

const styles = attributesStyles;

const ProductDetailsTopNAttributes: React.FC<Props> = ({ fields, product: { brand, attributeTypes } }) => {
    if (!brand && (!attributeTypes || attributeTypes.length === 0)) {
        return null;
    }

    const attributesToShow = fields.attributesToShow ?? 6;
    attributeTypes = attributeTypes?.slice().sort((a, b) => a.sortOrder - b.sortOrder);
    const limitedAttributeTypes = (attributeTypes ?? []).slice(0, attributesToShow);
    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="productDetails_attributes">
            <UnorderedList {...styles.unorderedList}>
                {limitedAttributeTypes.map((attributeType, index) => (
                    <ListItem key={attributeType.id.toString()}
                        data-test-selector="attributes_item"
                        data-attributetypeid={attributeType.id}  {...styles.listItem}
                    >
                        <span {...styles.label}>
                            <Typography data-test-selector="attributes_item_label" {...styles.attributeTypeLabelText}>
                                {attributeType.label || attributeType.name} :
                            </Typography>
                        </span>
                        <span>
                            <span data-test-selector="attributes_item_value">
                                {(attributeType.attributeValues ?? [])
                                    .map(attributeValue => attributeValue.valueDisplay)
                                    .join(", ")}
                            </span>
                        </span>
                    </ListItem>
                ))}
            </UnorderedList>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsTopNAttributes),
    definition: {
        displayName: "Top N Attributes",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.attributesToShow,
                displayName: "Number of attributes to show",
                editorTemplate: "IntegerField",
                defaultValue: 6,
                fieldType: "General"
            }
        ],
    }
};

export default widgetModule;
